import { untrack } from "svelte";
import type { HelperState } from "@otto-ec/otto-components-utils/svelte/useDomAnimationHelper";
import { sheetScope } from "../utils/props";
import { stack } from "../stack/stack.global.svelte";
import { dispatchSheetEvent } from "../event/event.dispatch";
import { AFTER_CLOSE, AFTER_OPEN, CLOSE, FLYIN, FLYOUT, OPEN } from "../event/event.name.defs";
import { canUseHtmlDialogElement } from "../../../../common/utils/canUseHtmlDialogElement";

const log = /*           */ sheetScope.scope("visibility");
const logDialog = /*           */ log.scope("dialog");
const logEvents = /*           */ log.scope("events");
const logTeleport = /*           */ log.scope("teleport");
const logDisplay = /*           */ log.scope("display");

export type OcSheetV1VisibilityState = HelperState<{
  /**
 *
 */
  isDisplay: boolean;
  /**
 *
 */
  isVisible: boolean;
  /**
 *
 */
  isShown: boolean;
}>;

type TeleportableSheet = HTMLOcSheetV1Element & {
  originalParent?: HTMLElement | null;
};

/**
 *
 *
 *
 *
 *
 */
export const displaySheetElement = (host: HTMLOcSheetV1Element): void => {
  if (import.meta.env.DEV) logDisplay.info("Display sheet element", host.id);
  host.style.display = "";
};

/**
 *
 *
 *
 *
 *
 */
export const hideSheetElement = (host: HTMLOcSheetV1Element): void => {
  if (import.meta.env.DEV) logDisplay.info("Hide sheet element", host.id);
  host.style.display = "none";
};

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export const showDialogModal = (
  element: HTMLDialogElement | null | undefined,
  host: TeleportableSheet,
): void => {
  if (canUseHtmlDialogElement && !element?.open) {
    if (import.meta.env.DEV) logDialog.info("Dialog Show", host.id);
    element?.showModal();
  }
};

/**
 *
 *
 *
 *
 *
 *
 *
 */
export const closeDialogModal = (
  element: HTMLDialogElement | null | undefined,
  host: TeleportableSheet,
): void => {
  if (canUseHtmlDialogElement && element?.open) {
    if (import.meta.env.DEV) logDialog.info("Dialog Close", host.id);
    element?.close();
  }
};

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export const teleportSheetToBody = (host: TeleportableSheet): void => {
  /*                                                                    */
  /*                                                     */
  if (host.parentElement === document.body || import.meta.env.STORYBOOK) return;

  /*                                                  */
  if (canUseHtmlDialogElement) return;

  /*                                                                     */
  host.originalParent = host.parentElement;

  if (import.meta.env.DEV) logTeleport.info("Teleporting sheet to body", host.id);
  document.body.appendChild(host);
};

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export const teleportSheetBack = (host: TeleportableSheet): void => {
  /*                                                                    */
  /*                                                     */
  if (!host.originalParent || import.meta.env.STORYBOOK) return;

  /*                                                       */
  if (canUseHtmlDialogElement) return;

  if (import.meta.env.DEV) logTeleport.info("Teleporting sheet back to original position", host.id);
  host.originalParent.append(host);
  host.originalParent = null;
};

/**
 *
 *
 *
 *
 *
 *
 *
 */
export const useSheetVisibility = (
  host: TeleportableSheet,
  visibilityState: OcSheetV1VisibilityState,
  getDialogElement: () => HTMLDialogElement | null | undefined,
): void => {
  const isShowAnimation = (): boolean => visibilityState.$animIn;
  const isCloseAnimation = (): boolean => visibilityState.$animOut;

  /*                                                                            */
  /*                                                                  */
  /*                                                                */
  $effect(() => {
    if (visibilityState.isDisplay && untrack(isShowAnimation)) {
      const [action, , reason, dialogElement] = untrack(
        () => [...stack.action, stack.reason, getDialogElement()] as const,
      );

      teleportSheetToBody(host);
      displaySheetElement(host);
      showDialogModal(dialogElement, host);

      /*                                */
      if (import.meta.env.DEV) logEvents.info(OPEN[1], host.id, reason, action);
      dispatchSheetEvent(OPEN, host, { reason, action, info: stack.info });
    }
  });

  /*                                                 */
  $effect(() => {
    if (visibilityState.isVisible && untrack(isShowAnimation)) {
      const [action, , reason] = untrack(() => [...stack.action, stack.reason] as const);
      if (import.meta.env.DEV) logEvents.info(FLYIN[1], host.id, reason, action);
      dispatchSheetEvent(FLYIN, host, { reason, action, info: stack.info });
    }
  });

  /*                                                      */
  $effect(() => {
    if (visibilityState.isShown && untrack(isShowAnimation)) {
      const [action, , reason] = untrack(() => [...stack.action, stack.reason] as const);
      if (import.meta.env.DEV) logEvents.info(AFTER_OPEN[1], host.id, reason, action);
      dispatchSheetEvent(AFTER_OPEN, host, { reason, action, info: stack.info });
    }
  });

  /*                                                          */
  $effect(() => {
    if (!visibilityState.isShown && untrack(isCloseAnimation)) {
      const [action, closeType, reason] = untrack(() => [...stack.action, stack.reason] as const);
      if (import.meta.env.DEV) logEvents.info(CLOSE[1], host.id, reason, action, closeType);
      dispatchSheetEvent(CLOSE, host, { reason, action, closeType, info: stack.info });
    }
  });

  /*                                                   */
  $effect(() => {
    if (!visibilityState.isVisible && untrack(isCloseAnimation)) {
      const [action, closeType, reason] = untrack(() => [...stack.action, stack.reason] as const);
      if (import.meta.env.DEV) logEvents.info(FLYOUT[1], host.id, reason, action, closeType);
      dispatchSheetEvent(FLYOUT, host, { reason, action, closeType, info: stack.info });
    }
  });

  /*                                                                                       */
  /*                                                                */
  /*                                                                               */
  /*                                   */
  $effect(() => {
    if (!visibilityState.isDisplay) {
      const [action, closeType, reason, dialogElement] = untrack(
        () => [...stack.action, stack.reason, getDialogElement()] as const,
      );

      /*                                                  */
      /*                                                                   */
      closeDialogModal(dialogElement, host);
      hideSheetElement(host);

      /*                                                            */
      /*                                                                                         */
      if (untrack(isCloseAnimation)) {
        teleportSheetBack(host);
        if (import.meta.env.DEV) logEvents.info(AFTER_CLOSE[1], host.id, reason, action, closeType);
        dispatchSheetEvent(AFTER_CLOSE, host, { reason, action, closeType, info: stack.info });
      }
    }
  });
};
